import React from 'react';
import { TestTube, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import type { ResendConfig, TestResults, ShowPasswords } from '../types';

interface ResendSettingsTabProps {
  config: ResendConfig;
  onChange: (config: ResendConfig) => void;
  testResults: TestResults;
  onTest: (service: string) => void;
  showPasswords: ShowPasswords;
  onTogglePassword: (field: string) => void;
}

const ResendSettingsTab: React.FC<ResendSettingsTabProps> = ({
  config,
  onChange,
  testResults,
  onTest,
  showPasswords,
  onTogglePassword
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Configuración Resend</h3>
          <p className="text-sm text-gray-600">Para envío de emails transaccionales del sistema</p>
        </div>
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <span className="text-sm font-medium text-gray-700">
              {config.enabled ? 'Habilitado' : 'Deshabilitado'}
            </span>
            <button
              type="button"
              onClick={() => onChange({ ...config, enabled: !config.enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
          {testResults.resend !== undefined && (
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
              testResults.resend ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {testResults.resend ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              <span className="text-sm">{testResults.resend ? 'Conectado' : 'Error'}</span>
            </div>
          )}
          <button
            onClick={() => onTest('resend')}
            disabled={!config.enabled}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TestTube className="w-4 h-4" />
            <span>Probar</span>
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key *</label>
          <div className="relative">
            <input
              type={showPasswords.resend ? 'text' : 'password'}
              value={config.apiKey}
              onChange={(e) => onChange({ ...config, apiKey: e.target.value })}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="re_..."
            />
            <button
              type="button"
              onClick={() => onTogglePassword('resend')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.resend ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email del Remitente *</label>
          <input
            type="email"
            value={config.fromEmail}
            onChange={(e) => onChange({ ...config, fromEmail: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="noreply@ceroideas.es"
          />
          <p className="mt-1 text-xs text-gray-500">
            El dominio debe estar verificado en tu cuenta de Resend
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Remitente</label>
          <input
            type="text"
            value={config.fromName}
            onChange={(e) => onChange({ ...config, fromName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Cero Ideas CRM"
          />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">¿Cómo obtener tu API Key de Resend?</h4>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Ve a <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a></li>
          <li>Inicia sesión o crea una cuenta</li>
          <li>Ve a API Keys en el panel de control</li>
          <li>Haz clic en "Create API Key"</li>
          <li>Dale un nombre (ej: "CRM Production")</li>
          <li>Selecciona los permisos: "Sending access"</li>
          <li>Copia la clave que empieza con "re_"</li>
        </ol>
        <div className="mt-3 pt-3 border-t border-blue-200">
          <h4 className="text-sm font-medium text-blue-900 mb-1">Verificar dominio:</h4>
          <p className="text-sm text-blue-700">
            Recuerda verificar tu dominio en Resend → Domains para poder enviar emails desde tu dirección.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResendSettingsTab;
